import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type VariantListOutput = OutputRouter['variants']['list']
export type Variant = VariantListOutput['variants'][number]
export type VariantDetails = OutputRouter['variants']['getById']
export type VariantStoreListOutput = OutputRouter['variants']['listForStore']

// Input types
export type ListVariantsInput = InputRouter['variants']['list']
export type ListVariantsForStoreInput = InputRouter['variants']['listForStore']
export type CreateVariantInput = InputRouter['variants']['create']
export type UpdateVariantInput = InputRouter['variants']['update']

// List all variants for account
export const useVariantsList = (
    params: Parameters<typeof trpc.variants.list.useQuery>[0]
) => {
    return trpc.variants.list.useQuery(params)
}


// List variants for specific store
export const useVariantsForStore = (
    params: Parameters<typeof trpc.variants.listForStore.useQuery>[0]
) => {
    return trpc.variants.listForStore.useQuery(params)
}

// Get single variant by ID
export const useVariantDetails = (id: string, enabled = true) => {
    return trpc.variants.getById.useQuery(
        { variantId: id },
        { enabled: enabled && !!id }
    )
}

// Create new variant
export const useCreateVariant = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.variants.create.useMutation({
        onSuccess: () => {
            utils.products.getDetails.invalidate()
            utils.products.getById.invalidate()
            utils.products.list.invalidate()
            utils.variants.list.invalidate()
            utils.variants.listForStore.invalidate()
            utils.variants.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Variant created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        },
    })
}

// Update variant
export const useUpdateVariant = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.variants.update.useMutation({
        onSuccess: () => {
            utils.products.getDetails.invalidate()
            utils.products.getById.invalidate()
            utils.products.list.invalidate()
            utils.variants.list.invalidate()
            utils.variants.listForStore.invalidate()
            utils.variants.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Variant updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        },
    })
}

// Sync variant to integration
export const useSyncVariantToIntegration = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.variants.syncToIntegration.useMutation({
        onSuccess: () => {
            utils.variants.list.invalidate()
            utils.variants.listForStore.invalidate()
            utils.variants.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Variant synced successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        },
    })
}

// Delete variant(s)
export const useDeleteVariants = () => {
    const utils = trpc.useUtils()

    return trpc.variants.delete.useMutation({
        onSuccess: () => {
            utils.variants.list.invalidate()
            utils.variants.listForStore.invalidate()
            utils.products.getDetails.invalidate()
        },
    })
}

